// src/utils/Polyslice.js
import * as THREE from "three";
import { sliceModelXZ } from "./sliceModel.js";

export class Polyslice {
  constructor(options = {}) {
    this.gcode = "";
    this.estimatedTime = 0;
    this.newline = "\n";
    this.autohome = options.autohome ?? true;
    this.workspacePlane = options.workspacePlane ?? "XY";
    this.timeUnit = options.timeUnit ?? "milliseconds";
    this.lengthUnit = options.lengthUnit ?? "millimeters";
    this.temperatureUnit = options.temperatureUnit ?? "celsius";
    this.nozzleTemperature = options.nozzleTemperature ?? 0;
    this.bedTemperature = options.bedTemperature ?? 0;
    this.fanSpeed = options.fanSpeed ?? 100;
  }

  getAutohome() { return this.autohome; }
  getWorkspacePlane() { return this.workspacePlane; }
  getTimeUnit() { return this.timeUnit; }
  getLengthUnit() { return this.lengthUnit; }
  getTemperatureUnit() { return this.temperatureUnit; }
  getNozzleTemperature() { return this.nozzleTemperature; }
  getBedTemperature() { return this.bedTemperature; }
  getFanSpeed() { return this.fanSpeed; }

  setAutohome(val = true) { this.autohome = !!val; return this; }
  setWorkspacePlane(val = "XY") {
    val = val.toUpperCase().trim();
    if (["XY", "XZ", "YZ"].includes(val)) this.workspacePlane = val;
    return this;
  }
  setTimeUnit(val = "milliseconds") {
    val = val.toLowerCase();
    if (["milliseconds", "seconds"].includes(val)) this.timeUnit = val;
    return this;
  }
  setLengthUnit(val = "millimeters") {
    val = val.toLowerCase();
    if (["millimeters", "inches"].includes(val)) this.lengthUnit = val;
    return this;
  }
  setTemperatureUnit(val = "celsius") {
    val = val.toLowerCase();
    if (["celsius", "fahrenheit", "kelvin"].includes(val)) this.temperatureUnit = val;
    return this;
  }
  setNozzleTemperature(val = 0) {
    if (typeof val === "number" && val >= 0) this.nozzleTemperature = val;
    return this;
  }
  setBedTemperature(val = 0) {
    if (typeof val === "number" && val >= 0) this.bedTemperature = val;
    return this;
  }
  setFanSpeed(val = 100) {
    if (typeof val === "number" && val >= 0 && val <= 100) this.fanSpeed = val;
    return this;
  }

  codeAutohome(x = null, y = null, z = null, skip = null, raise = null, leveling = null) {
    let g = "G28";
    if (x) g += " X";
    if (y) g += " Y";
    if (z) g += " Z";
    if (skip) g += " O";
    if (leveling) g += " L";
    if (typeof raise === "number") g += " R" + raise;
    return g + this.newline;
  }

  codeWorkspacePlane(val = null) {
    if (val !== null) this.setWorkspacePlane(val);
    return { XY: "G17", XZ: "G18", YZ: "G19" }[this.workspacePlane] + this.newline;
  }

  codeLengthUnit(val = null) {
    if (val !== null) this.setLengthUnit(val);
    return { millimeters: "G21", inches: "G20" }[this.lengthUnit] + this.newline;
  }

  codeTemperatureUnit(val = null) {
    if (val !== null) this.setTemperatureUnit(val);
    return { celsius: "M149 C", fahrenheit: "M149 F", kelvin: "M149 K" }[this.temperatureUnit] + this.newline;
  }

  codeMovement(x = null, y = null, z = null, e = null, f = null, s = null) {
    let g = "";
    if (x != null) g += ` X${x}`;
    if (y != null) g += ` Y${y}`;
    if (z != null) g += ` Z${z}`;
    if (e != null) g += ` E${e}`;
    if (f != null) g += ` F${f}`;
    if (s != null) g += ` S${s}`;
    return g;
  }

  codeLinearMovement(x = null, y = null, z = null, e = null, f = null, s = null) {
    const type = e == null ? "G0" : "G1";
    return type + this.codeMovement(x, y, z, e, f, s) + this.newline;
  }

  codeNozzleTemperature(temp = null, wait = true, index = null) {
    if (temp != null) this.setNozzleTemperature(temp);
    const t = this.nozzleTemperature;
    let g = wait ? "M109" : "M104";
    g += wait ? ` R${t}` : ` S${t}`;
    if (index != null) g += ` T${index}`;
    return g + this.newline;
  }

  codeBedTemperature(temp = null, wait = true, time = null) {
    if (temp != null) this.setBedTemperature(temp);
    const t = this.bedTemperature;
    let g = wait ? "M190" : "M140";
    g += wait ? ` R${t}` : ` S${t}`;
    if (wait && time != null && time > 0) {
      if (this.timeUnit === "milliseconds") time /= 1000;
      g += ` T${time}`;
    }
    return g + this.newline;
  }

  codeFanSpeed(speed = null, index = null) {
    if (speed != null) this.setFanSpeed(speed);
    let g = this.fanSpeed > 0 ? `M106 S${this.fanSpeed * 2.55}` : "M107";
    if (index != null) g += ` P${index}`;
    return g + this.newline;
  }

  async slice(mesh = null, options = {}) {
    this.gcode = `; Generated by CAMeleon\n; Using polyslice\n; ${new Date().toISOString()}\n\n`;
    this.gcode += this.codeLengthUnit();
    this.gcode += this.codeWorkspacePlane();
    this.gcode += this.codeTemperatureUnit();
    this.gcode += "; Setting temperatures\n";
    this.gcode += this.codeBedTemperature(options.bedTemperature ?? this.bedTemperature);
    this.gcode += this.codeNozzleTemperature(options.nozzleTemperature ?? this.nozzleTemperature);
    if (this.autohome) this.gcode += "; Auto homing\n" + this.codeAutohome();
    this.gcode += "; Setting fan speed\n" + this.codeFanSpeed(options.fanSpeed ?? this.fanSpeed);

    if (!mesh) return this.gcode;

    const lh = options.layerHeight ?? 0.2;
    const ps = options.printSpeed ?? 60;
    const extRate = 0.05;
    const nd = options.nozzleDiameter ?? 0.4;
    const id = options.infillDensity ?? 20;
    const spacing = nd * (100 / id);

    const bbox = new THREE.Box3().setFromObject(mesh);
    const size = bbox.getSize(new THREE.Vector3());
    const min = bbox.min;
    const layers = Math.ceil(size.y / lh);
    this.gcode += `; Total layers: ${layers}, Layer height: ${lh}mm\n`;

    let totalE = 0;

    for (let i = 0; i < layers; i++) {
      const y = min.y + i * lh;
      const result = await sliceModelXZ(mesh, null, y);
      if (!result?.contourPoints?.length) continue;
      this.gcode += `; Layer ${i + 1} at height ${y.toFixed(2)}mm\n`;
      for (const poly of result.contourPoints) {
        if (poly.length < 2) continue;
        let [x0, , z0] = poly[0];
        this.gcode += this.codeLinearMovement(x0, y, z0, null, ps * 60);
        for (let j = 1; j < poly.length; j++) {
          const [x1, , z1] = poly[j];
          const d = Math.hypot(x1 - x0, z1 - z0);
          totalE += d * extRate;
          this.estimatedTime += d / ps;
          this.gcode += this.codeLinearMovement(x1, y, z1, totalE, ps * 60);
        }
      }
      this.gcode += `; Infill for layer ${i + 1}\n`;
      for (let z = min.z; z <= min.z + size.z; z += spacing) {
        const xStart = min.x;
        const xEnd = min.x + size.x;
        this.gcode += this.codeLinearMovement(xStart, y, z, null, ps * 60);
        const len = xEnd - xStart;
        totalE += len * extRate;
        this.estimatedTime += (len / ps) * (id / 100);
        this.gcode += this.codeLinearMovement(xEnd, y, z, totalE, ps * 60);
      }
    }

    this.gcode += "; Print done\nM104 S0\nM140 S0\nG1 X0 Y220 F3000\nM84\n";
    const h = Math.floor(this.estimatedTime / 3600);
    const m = Math.floor((this.estimatedTime % 3600) / 60);
    const s = Math.floor(this.estimatedTime % 60);
    this.gcode += `\n; Estimated print time: ${h ? `${h}h ` : ""}${m}m ${s}s\n`;
    return { gcode: this.gcode, estimatedTime: this.estimatedTime };
  }
}
